/**
 * UpgradeCard Component
 * Displays upgrade options for itinerary days
 * WHY: Supports 4 types - choice (static), hotel/excursion/cruise (clickable modals)
 */

import React from "react";
import { Box, Typography, Stack } from "@mui/material";
import Image from "next/image";
import { useTranslations } from "next-intl";
import type { Upgrade } from "@/types/dto/pdp/pdp-itinerary";
import { ColletteChip } from "@collette-travel/collette-common-controls";
import ExcursionCard from "./excursion-card";

export interface UpgradeCardProps {
  upgrade: Upgrade;
  onClick?: (upgrade: Upgrade) => void;
  testIdPrefix: string;
  showChip?: boolean;
  wideCard?: boolean;
  // WHY: testIdPrefix allows unique IDs for multiple instances
}

const UpgradeCard: React.FC<UpgradeCardProps> = ({
  upgrade,
  onClick,
  testIdPrefix,
  showChip = true,
  wideCard = false
}) => {

  let type = "";
  if (upgrade.choiceOnTour && upgrade.choiceOnTour.length > 0) {
    type = "choice";
  } else if (upgrade.roomUpgrades && upgrade.roomUpgrades.length > 0) {
    type = "roomUpgrade";
  } else if (upgrade.excursion && upgrade.excursion.length > 0) {
    type = "excursion";
  }

  // WHY: S tring literals match the type values in mock data
  const isClickable = type !== "choice";

  const handleClick = () => {
    if (isClickable && onClick) {
      onClick(upgrade);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (isClickable && (event.key === "Enter" || event.key === " ")) {
      event.preventDefault();
      handleClick();
    }
  };

  const t = useTranslations("PDP");

  // Delegate excursion rendering to ExcursionCard
  if (type === "excursion" && upgrade.excursion?.length > 0) {
    return (
      <>
        {upgrade.excursion.map((exc, idx) => (
          <ExcursionCard
            key={exc.imageUrl ?? idx}
            excursion={exc}
            onClick={() => onClick?.(upgrade)}
            testIdPrefix={`${testIdPrefix}_${idx}`}
            showChip={showChip}
            wideCard={wideCard}
          />
        ))}
      </>
    );
  }

  // WHY: For tour choices, render each choice as a separate vertical card
  if (type === "choice" && upgrade.choiceOnTour?.length > 0) {
    return (
      <Stack gap="1rem">
        <Typography
          variant="subtitle_lg"
          sx={{ fontSize: "1.8rem", color: "primary.main" }}
          data-testid={`${testIdPrefix}_upgrade-card-section-title`}
        >
          {t("Choice on Tour Title")}
        </Typography>
        {upgrade.choiceOnTour.map((choice, index) => (
          <Box
            key={choice.title}
            data-testid={`${testIdPrefix}_upgrade-card_${index}`}
            sx={{
              display: "flex",
              gap: "2.4rem",
              backgroundColor: "background.default",
              borderRadius: "2rem",
              border: "1px solid",
              borderColor: "divider",
              cursor: "default",
              overflow: "hidden"
            }}
          >
            <Box
              sx={{
                position: "relative",
                width: "173px",
                height: "135px",
                flexShrink: 0,
                overflow: "hidden"
                // WHY: Fixed dimensions from Figma (173x135px), no border-radius as card overflow handles it
              }}
            >
              <Image
                src={choice.imageUrl ?? ""}
                alt={choice.title}
                fill
                sizes="173px"
                style={{ objectFit: "cover" }}
                data-testid={`${testIdPrefix}_upgrade-card-image_${index}`}
              />
            </Box>

            {/* Content */}
            <Stack
              flex={1}
              justifyContent="flex-start"
              gap="1.8rem"
              sx={{ padding: "1.2rem 1.2rem 1.2rem 0" }}

              // WHY: Padding only on text content area, not on image side
            >
              <ColletteChip
                color="info"
                label={t("Choice Option Label", { number: index + 1 })}
                testId={`${testIdPrefix}_inclusion-chip_${index}`}
                sx={{
                  fontSize: "1.4rem !important",
                  fontWeight: "600",
                  zIndex: 5,
                  borderRadius: "1rem",
                  width: "fit-content"
                }}
              />
              {/* Name */}
              <Typography
                variant="body1"
                sx={{
                  fontSize: "1.6rem",
                  fontWeight: 600,
                  lineHeight: 1.5,
                  color: "text.primary"
                }}
                data-testid={`${testIdPrefix}_upgrade-card-title_${index}`}
              >
                {choice.title}
              </Typography>
            </Stack>
          </Box>
        ))}
      </Stack>
    );
  }

  return (
    <Box
      data-testid={`${testIdPrefix}_upgrade-card_${type}`}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role={isClickable ? "button" : undefined}
      tabIndex={isClickable ? 0 : undefined}
      sx={{
        display: "flex",
        gap: "1.4rem",
        backgroundColor: "background.default",
        borderRadius: "2rem",
        border: "1px solid",
borderColor: "divider",
        cursor: isClickable ? "pointer" : "default",
        transition: "all 0.2s ease-in-out",
        overflow: "hidden",
        minHeight: "15rem",
        // WHY: Hover state only for clickable cards
        ...(isClickable && {
          "&:hover": {
            boxShadow: 6,
            transform: "translateY(-2px)"
          },
          "&:focus-visible": {
            outline: "2px solid var(--theme-palette-primary-main)",
            outlineOffset: "2px"
          }
        })
      }}
    >
      {type === "roomUpgrade" &&
        upgrade.roomUpgrades?.length > 0 &&
        upgrade.roomUpgrades.map((room) => (
          <React.Fragment key={room.title}>
            <Box
              sx={{
                position: "relative",
                width: "173px",
                height: { lg: "100%" },
                flexShrink: 0,
                overflow: "hidden"
                // WHY: Fixed dimensions from Figma (173x135px), no border-radius as card overflow handles it
              }}
            >
              <Image
                src={room.imageUrl ?? ""}
                alt={room.title}
                fill
                sizes="173px"
                style={{ objectFit: "cover" }}
              />
            </Box>

            {/* Content */}
            <Stack
              flex={1}
              justifyContent="center"
              gap="0.8rem"
              sx={{ padding: "1.2rem 1.2rem 1.2rem 0" }}
              // WHY: Padding only on text content area, not on image side
            >
              {/* Name */}
              <Typography
                variant="body1"
                sx={{
                  fontSize: "1.6rem",
                  fontWeight: 700,
                  lineHeight: 1.5,
                  color: "text.primary"
                }}
              >
                {room.title}
              </Typography>

              {/* Description */}
              <Typography
                variant="body2"
                sx={{
                  fontSize: "1.6rem",
                  fontWeight: 500,
                  lineHeight: 1.5
                }}
              >
                Room Upgrade Available
              </Typography>
              {isClickable && (
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: "1.6rem",
                    fontWeight: 700,
                    lineHeight: 1.5,
                    color: "primary.main",
                    textDecoration: "underline"
                    // WHY: Visual affordance that card is clickable
                  }}
                >
                  View Details
                </Typography>
              )}
            </Stack>
          </React.Fragment>
        ))}
    </Box>
  );
};

export default UpgradeCard;
